import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "IT Infrastructure & Networking",
  "Structured Cabling",
  "ATM Services",
  "POS Installation",
  "Security Cameras / Sensors",
  "Digital Signage",
  "Equipment Installation",
  "Technology Rollout",
  "Troubleshooting / Field Support",
  "Other",
];

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  location: "",
  service: "",
  num_locations: "",
  timeline: "",
  description: "",
};

const inputCls =
  "w-full bg-panel border border-line px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-signal focus:ring-1 focus:ring-signal transition-colors duration-300";

const labelCls =
  "block font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2";

function Field({ id, label, required, optional, children }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
        {required && <span className="text-signal"> *</span>}
        {optional && <span className="text-zinc-700 normal-case tracking-normal"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    document.title = "Contact — LEDOS Enterprise Solutions";
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { ...form };
      if (!payload.num_locations) delete payload.num_locations;
      if (!payload.timeline) delete payload.timeline;
      const res = await axios.post(`${API}/enquiries`, payload);
      setSubmitted(res.data.id);
      toast.success("Enquiry received. We will be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="relative bg-ink pt-44 pb-20 md:pb-28 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 grid-bg" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-signal mb-8">
              Contact
            </p>
          </MaskedLine>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
            <MaskedLine delay={0.25}>Have an installation or</MaskedLine>
            <MaskedLine delay={0.38}>
              <span>
                rollout project? <span className="text-signal">Tell us what you need.</span>
              </span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-ink pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 space-y-10">
              <div>
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
                  What happens next
                </p>
                <ul className="space-y-0 border-t border-line">
                  {[
                    "We review your project requirements",
                    "We follow up to discuss scope and site details",
                    "You get a clear plan for deployment",
                  ].map((item, i) => (
                    <li
                      key={item}
                      className="flex items-start gap-4 py-4 border-b border-line"
                    >
                      <span className="font-display text-xl font-black text-signal">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm text-zinc-400 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-line bg-panel p-6">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-signal mb-3">
                  Good to include
                </p>
                <p className="text-sm leading-relaxed text-zinc-500">
                  Equipment types, number of locations, site conditions and your target
                  timeline. The more detail you share, the faster we can scope the work.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8">
            {submitted ? (
              <div
                data-testid="enquiry-success-panel"
                className="border border-signal/40 bg-panel p-10 md:p-16 text-center"
              >
                <CheckCircle2 className="w-12 h-12 text-signal mx-auto mb-6" />
                <h2 className="font-display text-3xl font-black tracking-tight text-white mb-4">
                  Enquiry received.
                </h2>
                <p className="text-base text-zinc-400 max-w-md mx-auto mb-2">
                  Thank you — your project details are with the LEDOS team. We will review your
                  requirements and follow up.
                </p>
                <p className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-600">
                  Reference: <span className="text-signal">{submitted.slice(0, 8).toUpperCase()}</span>
                </p>
              </div>
            ) : (
              <form
                data-testid="enquiry-form"
                onSubmit={onSubmit}
                className="grid sm:grid-cols-2 gap-6"
              >
                <Field id="name" label="Name" required>
                  <input
                    id="name"
                    data-testid="enquiry-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    className={inputCls}
                    placeholder="Your full name"
                  />
                </Field>
                <Field id="company" label="Company" required>
                  <input
                    id="company"
                    data-testid="enquiry-input-company"
                    required
                    value={form.company}
                    onChange={set("company")}
                    className={inputCls}
                    placeholder="Company or organization"
                  />
                </Field>
                <Field id="email" label="Business Email" required>
                  <input
                    id="email"
                    type="email"
                    data-testid="enquiry-input-email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className={inputCls}
                    placeholder="you@company.com"
                  />
                </Field>
                <Field id="phone" label="Phone" required>
                  <input
                    id="phone"
                    type="tel"
                    data-testid="enquiry-input-phone"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    className={inputCls}
                    placeholder="Best number to reach you"
                  />
                </Field>
                <Field id="location" label="Project Location" required>
                  <input
                    id="location"
                    data-testid="enquiry-input-location"
                    required
                    value={form.location}
                    onChange={set("location")}
                    className={inputCls}
                    placeholder="City / site address or region"
                  />
                </Field>
                <Field id="service" label="Service Needed" required>
                  <select
                    id="service"
                    data-testid="enquiry-select-service"
                    required
                    value={form.service}
                    onChange={set("service")}
                    className={`${inputCls} appearance-none ${form.service ? "" : "text-zinc-600"}`}
                  >
                    <option value="" disabled>
                      Select a service
                    </option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s} className="text-white">
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field id="num_locations" label="Number of Locations" optional>
                  <input
                    id="num_locations"
                    data-testid="enquiry-input-num-locations"
                    value={form.num_locations}
                    onChange={set("num_locations")}
                    className={inputCls}
                    placeholder="e.g. 1, 5, 20+"
                  />
                </Field>
                <Field id="timeline" label="Preferred Project Timeline" optional>
                  <input
                    id="timeline"
                    data-testid="enquiry-input-timeline"
                    value={form.timeline}
                    onChange={set("timeline")}
                    className={inputCls}
                    placeholder="e.g. Next quarter, ASAP"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field id="description" label="Project Description" required>
                    <textarea
                      id="description"
                      data-testid="enquiry-input-description"
                      required
                      rows={6}
                      value={form.description}
                      onChange={set("description")}
                      className={`${inputCls} resize-y`}
                      placeholder="Tell us about the equipment, sites and work involved..."
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="enquiry-submit-button"
                    disabled={sending}
                    className="group inline-flex items-center justify-center gap-2 bg-signal text-black font-mono text-xs font-bold tracking-[0.15em] uppercase px-10 py-5 hover:-translate-y-1 transition-transform duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Submit Project Enquiry
                        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
